import { useState, useEffect } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { SettingsGroup } from '../../../shared/ui/SettingsGroup';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import { Slider } from '../../../components/ui/slider';
import {
  TemplateColorPicker,
  TemplateLogoUpload,
  TemplateFontSelector,
  type TemplateLogoPosition,
} from '../../../entities/template';
import type { FontAxis } from '../../../shared/lib/variableFontUtils';
import { getAxisStep } from '../../../shared/lib/variableFontUtils';

export type LogoPosition = TemplateLogoPosition;

interface TemplateFormData {
  accountId: string;
  backgroundColor: string;
  useGradient: boolean;
  backgroundColorEnd: string;
  backgroundAlpha: number;
  backgroundAlphaEnd: number;
  textColor: string;
  useTwoColors: boolean;
  textColorSecondary: string;
  textUppercase: boolean;
  textAlign: 'left' | 'center' | 'right';
  textBoxStyle: 'rounded' | 'square' | 'square-flush' | 'square-flush-right';
  textBoxAngle: number;
  textBoxRadius: number;
  textBoxPadding: number;
  textBoxPaddingTop: number;
  textBoxPaddingLeft: number;
  textBoxPaddingRight: number;
  fontBold: boolean;
  showLine: boolean;
  lineColor: string;
  lineHeight: number;
  linePosition: 'top' | 'bottom';
  textShadowEnabled: boolean;
  applyFontEffects: boolean;
  textShadowColor: string;
  textShadowBlur: number;
  textShadowWidth: number;
  textSoftShadowEnabled: boolean;
  textSoftShadowColor: string;
  textSoftShadowBlur: number;
  textSoftShadowOffsetX: number;
  textSoftShadowOffsetY: number;
  textSoftShadowOpacity: number;
  textShadowOpacity: number;
  logoSize?: number;
  textLineSpacing?: number;
  fontSize?: number;
  postLayout: 'bottom-bar' | 'top-card' | 'frame';
  accentColor: string;
  cardColor: string;
  cardInset: number;
  bandHeight: number;
  frameUrl?: string;
  frameTextX: number;
  frameTextY: number;
  frameTextWidth: number;
  frameTextVAlign: 'top' | 'center' | 'bottom';
  reelBackgroundUrl?: string;
}

interface PostTemplateSettingsProps {
  initialData?: TemplateFormData & {
    logoUrl?: string;
    fontUrl?: string;
    fontFamily?: string;
    fontName?: string;
    logoPosition?: LogoPosition;
    fontAxes?: FontAxis[];
    fontVariationSettings?: Record<string, number>;
    textShadowEnabled?: boolean;
    textShadowColor?: string;
    textShadowBlur?: number;
    textShadowWidth?: number;
    textSoftShadowEnabled?: boolean;
    textSoftShadowColor?: string;
    textSoftShadowBlur?: number;
    textSoftShadowOffsetX?: number;
    textSoftShadowOffsetY?: number;
    textSoftShadowOpacity?: number;
    textShadowOpacity?: number;
    logoSize?: number;
    textLineSpacing?: number;
  };
  onDataChange: (data: {
    formData: TemplateFormData;
    logoFile: File | null;
    logoPreview: string;
    logoPosition: LogoPosition;
    fontFile: File | null;
    fontFamily: string;
    fontName: string;
    fontUrl: string;
    fontAxes: FontAxis[];
    fontVariationSettings: Record<string, number>;
    frameFile: File | null;
    framePreview: string;
    reelBackgroundFile: File | null;
    reelBackgroundPreview: string;
  }) => void;
  /** Скрыть текстовые контролы (шрифт/цвет текста/обводка/тень/стили) — пост наследует их из reel. */
  hideTextControls?: boolean;
  /** Показать загрузку каркаса рилса (PNG 1080×1920) — для reel-таба. */
  showReelFrameUpload?: boolean;
}

export function PostTemplateSettings({
  initialData,
  onDataChange,
  hideTextControls = false,
  showReelFrameUpload = false,
}: PostTemplateSettingsProps) {
  const [formData, setFormData] = useState<TemplateFormData>({
    accountId: initialData?.accountId || '',
    backgroundColor: initialData?.backgroundColor || '#FFD700',
    useGradient: initialData?.useGradient !== undefined ? initialData.useGradient : false,
    backgroundColorEnd: initialData?.backgroundColorEnd || '#FFD700',
    backgroundAlpha: initialData?.backgroundAlpha !== undefined ? initialData.backgroundAlpha : 1,
    backgroundAlphaEnd: initialData?.backgroundAlphaEnd !== undefined ? initialData.backgroundAlphaEnd : 1,
    textColor: initialData?.textColor || '#000000',
    useTwoColors: initialData?.useTwoColors !== undefined ? initialData.useTwoColors : false,
    textColorSecondary: initialData?.textColorSecondary || '#FFFFFF',
    textUppercase: initialData?.textUppercase !== undefined ? initialData.textUppercase : false,
    textAlign: initialData?.textAlign ?? 'center',
    textBoxStyle: initialData?.textBoxStyle ?? 'rounded',
    textBoxAngle: initialData?.textBoxAngle !== undefined ? initialData.textBoxAngle : 0,
    textBoxRadius: initialData?.textBoxRadius !== undefined ? initialData.textBoxRadius : 30,
    textBoxPadding: initialData?.textBoxPadding !== undefined ? initialData.textBoxPadding : 12,
    textBoxPaddingTop: initialData?.textBoxPaddingTop ?? initialData?.textBoxPadding ?? 12,
    textBoxPaddingLeft: initialData?.textBoxPaddingLeft ?? initialData?.textBoxPadding ?? 12,
    textBoxPaddingRight: initialData?.textBoxPaddingRight ?? initialData?.textBoxPadding ?? 12,
    fontBold: initialData?.fontBold !== undefined ? initialData.fontBold : false,
    showLine: initialData?.showLine !== undefined ? initialData.showLine : false,
    lineColor: initialData?.lineColor || '#000000',
    lineHeight: initialData?.lineHeight !== undefined ? initialData.lineHeight : 1,
    linePosition: initialData?.linePosition || 'top',
    textShadowEnabled: initialData?.textShadowEnabled !== undefined ? initialData.textShadowEnabled : false,
    applyFontEffects: initialData?.applyFontEffects ?? true,
    textShadowColor: initialData?.textShadowColor || '#000000',
    textShadowBlur: initialData?.textShadowBlur !== undefined ? initialData.textShadowBlur : 4,
    textShadowWidth: initialData?.textShadowWidth !== undefined ? initialData.textShadowWidth : 4,
    textSoftShadowEnabled: initialData?.textSoftShadowEnabled !== undefined ? initialData.textSoftShadowEnabled : false,
    textSoftShadowColor: initialData?.textSoftShadowColor || '#000000',
    textSoftShadowBlur: initialData?.textSoftShadowBlur !== undefined ? initialData.textSoftShadowBlur : 8,
    textSoftShadowOffsetX: initialData?.textSoftShadowOffsetX !== undefined ? initialData.textSoftShadowOffsetX : 2,
    textSoftShadowOffsetY: initialData?.textSoftShadowOffsetY !== undefined ? initialData.textSoftShadowOffsetY : 4,
    textSoftShadowOpacity: initialData?.textSoftShadowOpacity !== undefined ? initialData.textSoftShadowOpacity : 100,
    textShadowOpacity: initialData?.textShadowOpacity !== undefined ? initialData.textShadowOpacity : 100,
    logoSize: initialData?.logoSize,
    textLineSpacing: initialData?.textLineSpacing,
    postLayout: initialData?.postLayout || 'bottom-bar',
    accentColor: initialData?.accentColor || '#FFD700',
    cardColor: initialData?.cardColor || '#FFFFFF',
    cardInset: initialData?.cardInset !== undefined ? initialData.cardInset : 32,
    bandHeight: initialData?.bandHeight !== undefined ? initialData.bandHeight : 0.22,
    frameUrl: initialData?.frameUrl,
    frameTextX: initialData?.frameTextX ?? 0.5,
    frameTextY: initialData?.frameTextY ?? 0.12,
    frameTextWidth: initialData?.frameTextWidth ?? 0.8,
    frameTextVAlign: initialData?.frameTextVAlign ?? 'top',
    reelBackgroundUrl: initialData?.reelBackgroundUrl,
    fontSize: initialData?.fontSize,
  });

  const [frameFile, setFrameFile] = useState<File | null>(null);
  const [framePreview, setFramePreview] = useState<string>(initialData?.frameUrl || '');
  const [reelBackgroundFile, setReelBackgroundFile] = useState<File | null>(null);
  const [reelBackgroundPreview, setReelBackgroundPreview] = useState<string>(initialData?.reelBackgroundUrl || '');

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>(initialData?.logoUrl || '');
  const [fontFile, setFontFile] = useState<File | null>(null);
  const [fontFamily, setFontFamily] = useState<string>(initialData?.fontFamily || 'Arial');
  const [fontName, setFontName] = useState<string>(initialData?.fontName || 'Arial');
  const [selectedFontUrl, setSelectedFontUrl] = useState<string>(initialData?.fontUrl || '');
  const [logoPosition, setLogoPosition] = useState<LogoPosition>(initialData?.logoPosition || 'top-left');
  const [fontAxes, setFontAxes] = useState<FontAxis[]>(initialData?.fontAxes || []);
  const [fontVariationSettings, setFontVariationSettings] = useState<Record<string, number>>(
    initialData?.fontVariationSettings || {}
  );

  // Уведомление родителя об изменениях
  useEffect(() => {
    onDataChange({
      formData,
      logoFile,
      logoPreview,
      logoPosition,
      fontFile,
      fontFamily,
      fontName,
      fontUrl: selectedFontUrl,
      fontAxes,
      fontVariationSettings,
      frameFile,
      framePreview,
      reelBackgroundFile,
      reelBackgroundPreview,
    });
  }, [
    formData,
    logoFile,
    logoPreview,
    logoPosition,
    fontFile,
    fontFamily,
    fontName,
    selectedFontUrl,
    fontAxes,
    fontVariationSettings,
    frameFile,
    framePreview,
    reelBackgroundFile,
    reelBackgroundPreview,
  ]);

  const handleLogoChange = (file: File, preview: string) => {
    setLogoFile(file);
    setLogoPreview(preview);
  };

  const handleLogoRemove = () => {
    setLogoFile(null);
    setLogoPreview('');
  };

  const handleFontSelect = (fontUrl: string, name: string, family: string) => {
    setSelectedFontUrl(fontUrl);
    setFontName(name);
    setFontFamily(family);
    setFontFile(null); // Сбрасываем загруженный файл
  };

  const handleFontFileUpload = (
    file: File,
    family: string,
    previewUrl: string,
    displayName: string,
    selectUrl: string
  ) => {
    setFontFile(file);
    setFontFamily(family);
    setFontName(displayName); // Имя загруженного шрифта (для списка и сохранения)
    setSelectedFontUrl(selectUrl); // Выбираем загруженный шрифт в выпадашке
  };

  const handleFontAxisChange = (axis: FontAxis, value: number) => {
    setFontVariationSettings((prev) => ({
      ...prev,
      [axis.tag]: value,
    }));
  };

  return (
    <div className="space-y-6">
      {/* Аккаунт задаёт страница (шапка «Шаблон @X»): второй выбор аккаунта
          здесь спорил с шапкой и мог перезаписать чужое оформление. */}
      <SettingsGroup title="ЦВЕТА" />

      {/* Цвета */}
      <TemplateColorPicker
        backgroundColor={formData.backgroundColor}
        onBackgroundColorChange={(color) => setFormData({ ...formData, backgroundColor: color })}
        useGradient={formData.useGradient}
        onUseGradientChange={(value) => setFormData({ ...formData, useGradient: value })}
        backgroundColorEnd={formData.backgroundColorEnd}
        onBackgroundColorEndChange={(color) => setFormData({ ...formData, backgroundColorEnd: color })}
        backgroundAlpha={formData.backgroundAlpha}
        onBackgroundAlphaChange={(alpha) => setFormData({ ...formData, backgroundAlpha: alpha })}
        backgroundAlphaEnd={formData.backgroundAlphaEnd}
        onBackgroundAlphaEndChange={(alpha) => setFormData({ ...formData, backgroundAlphaEnd: alpha })}
        textColor={formData.textColor}
        onTextColorChange={(color) => setFormData({ ...formData, textColor: color })}
        useTwoColors={formData.useTwoColors}
        onUseTwoColorsChange={(value) => setFormData({ ...formData, useTwoColors: value })}
        textColorSecondary={formData.textColorSecondary}
        onTextColorSecondaryChange={(color) => setFormData({ ...formData, textColorSecondary: color })}
        hideTextColors={hideTextControls}
      />

      <SettingsGroup title="ЛОГОТИП" />

      {/* Загрузка лого */}
      <TemplateLogoUpload
        logoPreview={logoPreview}
        onLogoChange={handleLogoChange}
        onLogoRemove={handleLogoRemove}
        logoPosition={logoPosition}
        onLogoPositionChange={setLogoPosition}
      />

      <SettingsGroup title="ТЕКСТ" />

      {/* Размер шрифта (свой на reel и на post) */}
      <div className="space-y-1.5">
        <Label className="text-sm">Размер шрифта</Label>
        <div className="flex items-center gap-2">
          <button type="button" className="rounded-md border px-3 py-1.5 text-sm"
            onClick={() => setFormData({ ...formData, fontSize: Math.max(8, Math.round((formData.fontSize ?? 40) - 2)) })}>
            A−
          </button>
          <span className="w-16 text-center text-sm tabular-nums">
            {formData.fontSize != null ? `${Math.round(formData.fontSize)}px` : 'авто'}
          </span>
          <button type="button" className="rounded-md border px-3 py-1.5 text-sm"
            onClick={() => setFormData({ ...formData, fontSize: Math.min(200, Math.round((formData.fontSize ?? 40) + 2)) })}>
            A+
          </button>
          {formData.fontSize != null && (
            <button type="button" className="text-sm text-muted-foreground underline"
              onClick={() => setFormData({ ...formData, fontSize: undefined })}>
              Авто
            </button>
          )}
        </div>
      </div>

      {/* Выравнивание текста (своё на reel и на post) */}
      <div className="space-y-1.5">
        <Label className="text-sm">Выравнивание текста</Label>
        <div className="grid grid-cols-3 gap-2">
          {([['left', 'Слева'], ['center', 'Центр'], ['right', 'Справа']] as const).map(([val, label]) => (
            <button
              key={val}
              type="button"
              onClick={() => setFormData({ ...formData, textAlign: val })}
              className={`rounded-md border px-2 py-1.5 text-sm ${formData.textAlign === val ? 'border-primary bg-primary/10 font-medium' : 'border-input'}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <SettingsGroup title="ПЛАШКА И МАКЕТ" />

      {/* Стиль фон-бокса текста (reels) */}
      {showReelFrameUpload && (
        <div className="space-y-1.5">
          <Label className="text-sm">Фон текста</Label>
          <div className="grid grid-cols-2 gap-2">
            {([
              ['rounded', 'Скруглённый'],
              ['square-flush', 'Прямой у края'],
              ['square-flush-right', 'Прямой у края (справа)'],
            ] as const).map(([val, label]) => (
              <button
                key={val}
                type="button"
                onClick={() => setFormData({ ...formData, textBoxStyle: val })}
                className={`rounded-md border px-2 py-1.5 text-sm ${
                  formData.textBoxStyle === val ? 'border-primary bg-primary/10 font-medium' : 'border-input'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {/* Радиус скругления — % от высоты бокса (0 = острые, 100 = капсула).
              Не зависит от отступа — округлость постоянна. */}
          <div className="space-y-2 pt-1">
            <Label className="text-sm">Радиус скругления: {Math.round(formData.textBoxRadius)}%</Label>
            <Slider value={[formData.textBoxRadius]} min={0} max={100} step={1}
              onValueChange={([v]) => setFormData({ ...formData, textBoxRadius: v })} />
          </div>
          {/* Отступ текста от краёв бокса — по сторонам (низ = верх) */}
          <div className="space-y-2 pt-1">
            <Label className="text-sm">Отступ сверху/снизу: {Math.round(formData.textBoxPaddingTop)}px</Label>
            <Slider value={[formData.textBoxPaddingTop]} min={0} max={100} step={1}
              onValueChange={([v]) => setFormData({ ...formData, textBoxPaddingTop: v })} />
          </div>
          <div className="space-y-2 pt-1">
            <Label className="text-sm">Отступ слева: {Math.round(formData.textBoxPaddingLeft)}px</Label>
            <Slider value={[formData.textBoxPaddingLeft]} min={0} max={100} step={1}
              onValueChange={([v]) => setFormData({ ...formData, textBoxPaddingLeft: v })} />
          </div>
          <div className="space-y-2 pt-1">
            <Label className="text-sm">Отступ справа: {Math.round(formData.textBoxPaddingRight)}px</Label>
            <Slider value={[formData.textBoxPaddingRight]} min={0} max={100} step={1}
              onValueChange={([v]) => setFormData({ ...formData, textBoxPaddingRight: v })} />
          </div>
          {/* Наклон плашки — на все стили */}
          <div className="space-y-2 pt-1">
            <Label className="text-sm">Угол наклона: {Math.round(formData.textBoxAngle)}°</Label>
            <Slider value={[formData.textBoxAngle]} min={-15} max={15} step={1}
              onValueChange={([v]) => setFormData({ ...formData, textBoxAngle: v })} />
          </div>
        </div>
      )}

      {/* Каркас рилса (PNG 1080×1920) */}
      {showReelFrameUpload && (
        <div className="space-y-2 rounded-lg border p-4">
          <Label htmlFor="reelFrameFile">Каркас рилса (PNG 1080×1920)</Label>
          <input
            id="reelFrameFile"
            type="file"
            accept="image/png"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) { setFrameFile(f); setFramePreview(URL.createObjectURL(f)); }
            }}
          />
          {framePreview && (
            <div className="relative mt-2 inline-block">
              <img src={framePreview} alt="frame" className="max-h-40 rounded border" />
              <button
                type="button"
                aria-label="Удалить каркас"
                onClick={() => {
                  setFrameFile(null);
                  setFramePreview('');
                  setFormData((prev) => ({ ...prev, frameUrl: '' }));
                }}
                className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-black/70 text-sm leading-none text-white hover:bg-black"
              >
                ×
              </button>
            </div>
          )}
          <p className="text-xs text-muted-foreground">PNG с прозрачностью, рисуется поверх видео.</p>
        </div>
      )}

      {/* Фон рилса (виден, когда видео меньше формата рилса) */}
      {showReelFrameUpload && (
        <div className="space-y-2 rounded-lg border p-4">
          <Label htmlFor="reelBackgroundFile">Фон рилса</Label>
          <input
            id="reelBackgroundFile"
            type="file"
            accept="image/png,image/jpeg"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) { setReelBackgroundFile(f); setReelBackgroundPreview(URL.createObjectURL(f)); }
            }}
          />
          {reelBackgroundPreview && (
            <div className="relative mt-2 inline-block">
              <img src={reelBackgroundPreview} alt="reel background" className="max-h-40 rounded border" />
              <button
                type="button"
                aria-label="Удалить фон рилса"
                onClick={() => {
                  setReelBackgroundFile(null);
                  setReelBackgroundPreview('');
                  setFormData((prev) => ({ ...prev, reelBackgroundUrl: '' }));
                }}
                className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-black/70 text-sm leading-none text-white hover:bg-black"
              >
                ×
              </button>
            </div>
          )}
          <p className="text-xs text-muted-foreground">Виден только когда видео меньше формата рилса (1080×1920).</p>
        </div>
      )}

      {/* Макет поста (только пост) */}
      {!showReelFrameUpload && (
      <div className="space-y-2">
        <Label htmlFor="postLayout">Макет</Label>
        <Select
          value={formData.postLayout}
          onValueChange={(value) => setFormData({ ...formData, postLayout: value as 'bottom-bar' | 'top-card' })}
        >
          <SelectTrigger id="postLayout">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="bottom-bar">Плашка снизу</SelectItem>
            <SelectItem value="top-card">Плашка сверху</SelectItem>
            <SelectItem value="frame">Каркас (PNG)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      )}

      {/* Настройки макета frame */}
      {formData.postLayout === 'frame' && (
        <div className="space-y-4 rounded-lg border p-4">
          <div className="space-y-2">
            <Label htmlFor="frameFile">Каркас (PNG 1080×1350)</Label>
            <input
              id="frameFile"
              type="file"
              accept="image/png"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) { setFrameFile(f); setFramePreview(URL.createObjectURL(f)); }
              }}
            />
            {framePreview && (
              <div className="relative mt-2 inline-block">
                <img src={framePreview} alt="frame" className="max-h-40 rounded border" />
                <button
                  type="button"
                  aria-label="Удалить каркас"
                  onClick={() => {
                    setFrameFile(null);
                    setFramePreview('');
                    setFormData((prev) => ({ ...prev, frameUrl: '' }));
                  }}
                  className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-black/70 text-sm leading-none text-white hover:bg-black"
                >
                  ×
                </button>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Текст X: {Math.round(formData.frameTextX * 100)}%</Label>
            <Slider value={[formData.frameTextX]} min={0} max={1} step={0.01}
              onValueChange={([v]) => setFormData({ ...formData, frameTextX: v })} />
          </div>
          <div className="space-y-2">
            <Label>Текст Y: {Math.round(formData.frameTextY * 100)}%</Label>
            <Slider value={[formData.frameTextY]} min={0} max={1} step={0.01}
              onValueChange={([v]) => setFormData({ ...formData, frameTextY: v })} />
          </div>
          <div className="space-y-2">
            <Label>Ширина текста: {Math.round(formData.frameTextWidth * 100)}%</Label>
            <Slider value={[formData.frameTextWidth]} min={0.1} max={1} step={0.01}
              onValueChange={([v]) => setFormData({ ...formData, frameTextWidth: v })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Вертикаль текста</Label>
            <div className="grid grid-cols-3 gap-2">
              {([['top', 'Верх'], ['center', 'Центр'], ['bottom', 'Низ']] as const).map(([val, label]) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => setFormData({ ...formData, frameTextVAlign: val })}
                  className={`rounded-md border px-2 py-1.5 text-sm ${formData.frameTextVAlign === val ? 'border-primary bg-primary/10 font-medium' : 'border-input'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Настройки макета top-card */}
      {formData.postLayout === 'top-card' && (
        <div className="space-y-4 rounded-lg border p-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="accentColor">Цвет подложки</Label>
              <input
                id="accentColor"
                type="color"
                value={formData.accentColor}
                onChange={(e) => setFormData({ ...formData, accentColor: e.target.value })}
                className="h-10 w-full rounded-md border cursor-pointer"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cardColor">Цвет карточки</Label>
              <input
                id="cardColor"
                type="color"
                value={formData.cardColor}
                onChange={(e) => setFormData({ ...formData, cardColor: e.target.value })}
                className="h-10 w-full rounded-md border cursor-pointer"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Боковой отступ карточки: {formData.cardInset}px</Label>
            <Slider
              value={[formData.cardInset]}
              min={0}
              max={120}
              step={4}
              onValueChange={([v]) => setFormData({ ...formData, cardInset: v })}
            />
          </div>
          <div className="space-y-2">
            <Label>Высота плашки: {Math.round(formData.bandHeight * 100)}%</Label>
            <Slider
              value={[formData.bandHeight]}
              min={0.15}
              max={0.35}
              step={0.01}
              onValueChange={([v]) => setFormData({ ...formData, bandHeight: v })}
            />
          </div>
        </div>
      )}




      <SettingsGroup title="ОФОРМЛЕНИЕ ТЕКСТА" />

      {/* Тумблер: применять ли наследованные эффекты шрифта (обводка/тень) к посту */}
      {hideTextControls && (
        <div className="flex items-center gap-2 pt-1">
          <Checkbox
            id="applyFontEffects"
            checked={formData.applyFontEffects}
            onCheckedChange={(checked) =>
              setFormData({ ...formData, applyFontEffects: checked === true })
            }
          />
          <label htmlFor="applyFontEffects" className="text-sm cursor-pointer">
            Применить настройки шрифта (обводка, тень)
          </label>
        </div>
      )}

      {/* Регистр текста у поста свой — не наследуется из reel */}
      {hideTextControls && (
        <div className="flex items-center gap-2 pt-1">
          <Checkbox
            id="postTextUppercase"
            checked={formData.textUppercase}
            onCheckedChange={(checked) =>
              setFormData({ ...formData, textUppercase: checked === true })
            }
          />
          <label htmlFor="postTextUppercase" className="text-sm cursor-pointer">
            Заглавные буквы
          </label>
        </div>
      )}

      {/* Загрузка шрифта — скрыто для поста (наследует из reel) */}
      {!hideTextControls && (
      <TemplateFontSelector
        selectedFontUrl={selectedFontUrl}
        onFontSelect={handleFontSelect}
        onFontFileUpload={handleFontFileUpload}
        initialFontUrl={initialData?.fontUrl}
        initialFontName={initialData?.fontName}
        onAxesDetected={(axes) => {
          setFontAxes(axes);
          // Инициализируем значения осей по умолчанию если ещё не заданы
          if (axes.length > 0 && Object.keys(fontVariationSettings).length === 0) {
            const defaults: Record<string, number> = {};
            axes.forEach(a => { defaults[a.tag] = a.default; });
            setFontVariationSettings(defaults);
          }
        }}
      />
      )}

      {/* Variable Font оси */}
      {!hideTextControls && fontAxes.length > 0 && (
        <div className="space-y-3">
          <Label>Оси Variable Font</Label>
          <div className="space-y-4">
            {fontAxes.map((axis) => {
              const currentValue = fontVariationSettings[axis.tag] ?? axis.default;
              const step = getAxisStep(axis.tag, axis.min, axis.max);
              return (
                <div key={axis.tag} className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-sm">{axis.name}</label>
                    <span className="text-xs text-muted-foreground font-mono">
                      {Number.isInteger(currentValue) ? currentValue : currentValue.toFixed(2)}
                    </span>
                  </div>
                  <Slider
                    min={axis.min}
                    max={axis.max}
                    step={step}
                    value={[currentValue]}
                    onValueChange={(values) => handleFontAxisChange(axis, values[0])}
                  />
                  <div className="flex justify-between text-[10px] text-muted-foreground">
                    <span>{axis.min}</span>
                    <span>{axis.max}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Стили текста — скрыто для поста (наследует из reel) */}
      {!hideTextControls && (
      <div className="space-y-3">
        <Label>Стили текста</Label>
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="textUppercase"
              checked={formData.textUppercase}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, textUppercase: checked === true })
              }
            />
            <label
              htmlFor="textUppercase"
              className="text-sm cursor-pointer"
            >
              Заглавные буквы
            </label>
          </div>
          {/* Показываем fontBold только если нет variable font осей (нет wght) */}
          {!fontAxes.some(a => a.tag === 'wght') && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id="fontBold"
                checked={formData.fontBold}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, fontBold: checked === true })
                }
              />
              <label
                htmlFor="fontBold"
                className="text-sm cursor-pointer"
              >
                Жирный шрифт
              </label>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Обводка текста — скрыто для поста (наследует из reel) */}
      {!hideTextControls && (
      <div className="space-y-3">
        <Label>Обводка текста</Label>
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="textShadowEnabled"
              checked={formData.textShadowEnabled}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, textShadowEnabled: checked === true })
              }
            />
            <label htmlFor="textShadowEnabled" className="text-sm cursor-pointer">
              Обводка текста
            </label>
          </div>

          {formData.textShadowEnabled && (
            <div className="space-y-3 pl-6">
              <div className="space-y-2">
                <Label htmlFor="textShadowColor">Цвет тени</Label>
                <input
                  id="textShadowColor"
                  type="color"
                  value={formData.textShadowColor}
                  onChange={(e) => setFormData({ ...formData, textShadowColor: e.target.value })}
                  className="h-10 w-full cursor-pointer rounded-md border"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Толщина</Label>
                  <span className="text-xs text-muted-foreground font-mono">{formData.textShadowWidth}px</span>
                </div>
                <Slider
                  min={0}
                  max={20}
                  step={1}
                  value={[formData.textShadowWidth]}
                  onValueChange={(values) => setFormData({ ...formData, textShadowWidth: values[0] })}
                />
                <div className="flex justify-between text-[10px] text-muted-foreground">
                  <span>0</span>
                  <span>20</span>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Размытие</Label>
                  <span className="text-xs text-muted-foreground font-mono">{formData.textShadowBlur}px</span>
                </div>
                <Slider
                  min={0}
                  max={20}
                  step={1}
                  value={[formData.textShadowBlur]}
                  onValueChange={(values) => setFormData({ ...formData, textShadowBlur: values[0] })}
                />
                <div className="flex justify-between text-[10px] text-muted-foreground">
                  <span>0</span>
                  <span>20</span>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Непрозрачность</Label>
                  <span className="text-xs text-muted-foreground font-mono">{formData.textShadowOpacity}%</span>
                </div>
                <Slider
                  min={0}
                  max={100}
                  step={1}
                  value={[formData.textShadowOpacity]}
                  onValueChange={(values) => setFormData({ ...formData, textShadowOpacity: values[0] })}
                />
                <div className="flex justify-between text-[10px] text-muted-foreground">
                  <span>0</span>
                  <span>100</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Мягкая тень текста — скрыто для поста (наследует из reel) */}
      {!hideTextControls && (
      <div className="space-y-3">
        <Label>Мягкая тень</Label>
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="textSoftShadowEnabled"
              checked={formData.textSoftShadowEnabled}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, textSoftShadowEnabled: checked === true })
              }
            />
            <label htmlFor="textSoftShadowEnabled" className="text-sm cursor-pointer">
              Мягкая тень
            </label>
          </div>

          {formData.textSoftShadowEnabled && (
            <div className="space-y-3 pl-6">
              <div className="space-y-2">
                <Label htmlFor="textSoftShadowColor">Цвет тени</Label>
                <input
                  id="textSoftShadowColor"
                  type="color"
                  value={formData.textSoftShadowColor}
                  onChange={(e) => setFormData({ ...formData, textSoftShadowColor: e.target.value })}
                  className="h-10 w-full cursor-pointer rounded-md border"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Размытие</Label>
                  <span className="text-xs text-muted-foreground font-mono">{formData.textSoftShadowBlur}px</span>
                </div>
                <Slider
                  min={0}
                  max={40}
                  step={1}
                  value={[formData.textSoftShadowBlur]}
                  onValueChange={(values) => setFormData({ ...formData, textSoftShadowBlur: values[0] })}
                />
                <div className="flex justify-between text-[10px] text-muted-foreground">
                  <span>0</span>
                  <span>40</span>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Смещение X</Label>
                  <span className="text-xs text-muted-foreground font-mono">{formData.textSoftShadowOffsetX}px</span>
                </div>
                <Slider min={-40} max={40} step={1} value={[formData.textSoftShadowOffsetX]}
                  onValueChange={(values) => setFormData({ ...formData, textSoftShadowOffsetX: values[0] })} />
                <div className="flex justify-between text-[10px] text-muted-foreground"><span>-40</span><span>40</span></div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Смещение Y</Label>
                  <span className="text-xs text-muted-foreground font-mono">{formData.textSoftShadowOffsetY}px</span>
                </div>
                <Slider min={-40} max={40} step={1} value={[formData.textSoftShadowOffsetY]}
                  onValueChange={(values) => setFormData({ ...formData, textSoftShadowOffsetY: values[0] })} />
                <div className="flex justify-between text-[10px] text-muted-foreground"><span>-40</span><span>40</span></div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Непрозрачность</Label>
                  <span className="text-xs text-muted-foreground font-mono">{formData.textSoftShadowOpacity}%</span>
                </div>
                <Slider min={0} max={100} step={1} value={[formData.textSoftShadowOpacity]}
                  onValueChange={(values) => setFormData({ ...formData, textSoftShadowOpacity: values[0] })} />
                <div className="flex justify-between text-[10px] text-muted-foreground"><span>0</span><span>100</span></div>
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      <SettingsGroup title="ДОПОЛНИТЕЛЬНО" />

      {/* Размер лого */}
      <div className="space-y-2">
        <Label htmlFor="logoSize">Размер лого (px)</Label>
        <input
          id="logoSize"
          type="number"
          min={80}
          max={500}
          step={4}
          placeholder="240"
          value={formData.logoSize ?? ''}
          onChange={(e) =>
            setFormData({
              ...formData,
              logoSize: e.target.value === '' ? undefined : Number(e.target.value),
            })
          }
          className="h-10 w-full rounded-md border px-3"
        />
      </div>

      {/* Межстрочный интервал — скрыто для поста (наследует из reel) */}
      {!hideTextControls && (
      <div className="space-y-2">
        <Label htmlFor="textLineSpacing">Межстрочный интервал</Label>
        <input
          id="textLineSpacing"
          type="number"
          min={0.8}
          max={2}
          step={0.05}
          placeholder="1.0"
          value={formData.textLineSpacing ?? ''}
          onChange={(e) =>
            setFormData({
              ...formData,
              textLineSpacing: e.target.value === '' ? undefined : Number(e.target.value),
            })
          }
          className="h-10 w-full rounded-md border px-3"
        />
      </div>
      )}

      {/* Декоративная линия */}
      <div className="space-y-3">
        <Label>Декоративная линия</Label>
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="showLine"
              checked={formData.showLine}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, showLine: checked === true })
              }
            />
            <label
              htmlFor="showLine"
              className="text-sm cursor-pointer"
            >
              Показать линию
            </label>
          </div>

          {formData.showLine && (
            <div className="space-y-3 pl-6">
              <div className="space-y-2">
                <Label htmlFor="linePosition">Расположение линии</Label>
                <Select
                  value={formData.linePosition}
                  onValueChange={(value: 'top' | 'bottom') =>
                    setFormData({ ...formData, linePosition: value })
                  }
                >
                  <SelectTrigger id="linePosition">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="top">Сверху</SelectItem>
                    <SelectItem value="bottom">Снизу</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="lineColor">Цвет линии</Label>
                <input
                  id="lineColor"
                  type="color"
                  value={formData.lineColor}
                  onChange={(e) => setFormData({ ...formData, lineColor: e.target.value })}
                  className="h-10 w-full cursor-pointer rounded-md border"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lineHeight">Толщина линии (px)</Label>
                <input
                  id="lineHeight"
                  type="number"
                  min="1"
                  max="16"
                  value={formData.lineHeight}
                  onChange={(e) =>
                    setFormData({ ...formData, lineHeight: parseInt(e.target.value) || 1 })
                  }
                  className="h-10 w-full rounded-md border px-3"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}